import Splide from '@splidejs/splide';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import Vue from '../../assets/vue';
import { setMaxHeightStyle } from '../../scripts/utils/setMaxHeightStyle';
import { addItems } from '../../scripts/cartApi';
import { sendCustomEvent } from '../../scripts/config/analytics';
import {
  updateAvailabilityAddToCartNavBtns,
  forceProductVideoStart,
  videoToggle,
  videoEnded,
  getVariantIdByUrl,
  formatPriceWithoutZeros,
  normalizeProductConfig,
  addOffsetToStickyProductSlider,
  INVENTORY_POLICIES,
  updateProductUrl,
  calcPreOrderMessage,
  isBundleAvailable,
  calcBundlePreOrderMessage,
  calcBundleVariantsForSell,
  calcSalePrice,
  calcNetPrice,
  ADD_PRODUCT_TO_CART_ERROR_MSG,
  findUnavailableBundleVariant,
  formatPriceWithoutDecimals,
} from '../../scripts/utils/product-helpers';
import {
  initCart,
  openSideCart,
} from '../../templates/common/side-cart/side-cart';
import {
  initProductBuyBanner,
  showBannerSpinner,
} from '../../templates/common/product-buy-banner/product-buy-banner';
import { calcBogoBonusProducts } from '../../scripts/utils/bogo-helpers';
import {
  PRODUCT_TYPE,
  ADD_TO_CART_TEXT,
  NOT_AVAILABLE_TEXT,
  PRE_ORDER_TEXT,
} from '../../scripts/config/constants';
import { initProductWishListModal } from '../product-wish-list-modal/product-wish-list-modal';

const { DeviceHelper, productsConfig } = window;

const initSlider = (selector) =>
  new Splide(selector, {
    type: 'loop',
    pagination: true,
    arrows: 'slider',
    accessibility: true,
    trimSpace: true,
    perMove: 1,
    flickVelocityThreshold: 0.5,
    flickPower: 500,
    slideFocus: false,
    updateOnMove: true,
    cover: false,
    focusableNodes: 'button',
    lazyLoad: 'nearby',
    classes: {
      pagination: 'product-base-dimmer__slider-main-pagination',
      page: 'product-base-dimmer__slider-main-pagination-btn',
    },
  });

const initThumbSlider = (selector) =>
  new Splide(selector, {
    type: 'loop',
    direction: 'ttb',
    height: '100%',
    gap: 10,
    pagination: false,
    accessibility: true,
    isNavigation: true,
    perPage: 6,
    flickVelocityThreshold: 0.5,
    flickPower: 500,
    wheel: true,
    wheelSleep: 300,
    lazyLoad: 'nearby',
  });

const findActiveVariantByVariantId = (variants, variantId) =>
  variants.find(({ id }) => Number(id) === Number(variantId)) || variants[0];

const findActiveVariantByOption = (variants, activeOption) =>
  variants.find(
    ({ handleizedOption1 }) => handleizedOption1 === activeOption
  ) || variants[0];

const Product = {
  data() {
    const {
      activeProductId,
      products,
      productsMeta,
      variantsMeta,
      variantsInventory,
      bundleConfig,
      warrantyConfig,
    } = productsConfig;
    const productActiveConfig = products[activeProductId];
    const normalizedProductActive = normalizeProductConfig(productActiveConfig);

    const variantIdByUrl = getVariantIdByUrl(productActiveConfig);

    const normalizedVariantActive = findActiveVariantByVariantId(
      normalizedProductActive.variants,
      variantIdByUrl
    );

    const variantActiveOption = normalizedVariantActive.handleizedOption1;
    const variantActiveIndex =
      normalizedProductActive.variants.indexOf(normalizedVariantActive) || 0;

    this.normalizedRelatedProducts = Object.values(products).reduce(
      (acc, product) => {
        acc.push(normalizeProductConfig(product));
        return acc;
      },
      []
    );

    const isTabletViewport = DeviceHelper.isTabletViewport();

    this.buyBannerProducts = this.normalizedRelatedProducts.reduce(
      (acc, normalizedRelatedProduct) => {
        acc.push({
          titleShort:
            productsMeta[normalizedRelatedProduct.id].buyBannerTitleShort,
          productId: normalizedRelatedProduct.id,
        });

        return acc;
      },
      []
    );

    return {
      normalizedProductActive,
      normalizedVariantActive,
      variantActiveOption,
      variantActiveIndex,
      variantsMeta,
      variantsInventory,
      bundleConfig,
      warrantyConfig,
      isTabletViewport,
      isSpinnerMainProductActive: false,
      isDetailsBlockClose: true,
      isDescriptionBlockClose: true,
      isBenefitsBlockClose: true,
      errorMessage: '',
      loaded: false,
      hasWarranty: false,
    };
  },
  computed: {
    productActiveMeta() {
      return productsConfig.productsMeta[this.normalizedProductActive.id];
    },
    variantActiveMeta() {
      return this.variantsMeta[this.normalizedVariantActive.id];
    },
    variantActiveBundleConfig() {
      return this.bundleConfig[this.normalizedVariantActive.id];
    },
    variantActiveWarrantyConfig() {
      return this.warrantyConfig[this.normalizedVariantActive.id] || null;
    },
    variantActiveInventory() {
      return this.variantsInventory[this.normalizedVariantActive.id];
    },
    isVariantActiveAvailable() {
      const isActiveVariantInStock = this.isVariantInStock(
        this.normalizedVariantActive
      );
      const isErrorEmpty = this.errorMessage === '';

      return isActiveVariantInStock && isErrorEmpty;
    },
    sliderMedias() {
      return (
        productsConfig.variantsMedia[this.normalizedVariantActive.id]
          .sliderMedia || []
      );
    },
    isBundle() {
      return this.normalizedProductActive.type === PRODUCT_TYPE.BUNDLE;
    },
    preOrderMessage() {
      const { preOrderText } = this.variantActiveMeta;

      if (this.isBundle) {
        return calcBundlePreOrderMessage(
          this.variantActiveBundleConfig,
          preOrderText
        );
      }

      const { inventoryQuantity, inventoryPolicy } =
        this.variantActiveInventory;

      return calcPreOrderMessage(
        preOrderText,
        inventoryQuantity,
        inventoryPolicy
      );
    },
    submitButtonText() {
      if (!this.isVariantActiveAvailable) {
        return this.variantActiveMeta.notAvailableText || NOT_AVAILABLE_TEXT;
      }

      if (this.preOrderMessage) {
        return PRE_ORDER_TEXT;
      }

      return ADD_TO_CART_TEXT;
    },
    swatchesList() {
      return this.normalizedProductActive.variants.reduce(
        (swatchesData, variant) => {
          swatchesData.push({
            available: this.isVariantInStock(variant),
            variantId: variant.id,
            optionValue: variant.handleizedOption1,
          });

          return swatchesData;
        },
        []
      );
    },
    activeNetPrice() {
      const { price } = this.normalizedVariantActive;

      return calcNetPrice(
        price,
        this.normalizedVariantActive.compare_at_price,
        this.activeDiscountPrice
      );
    },
    formattedActiveNetPrice() {
      return formatPriceWithoutZeros(this.activeNetPrice);
    },
    activeDiscountPrice() {
      const { discountPrice } = this.variantActiveMeta;

      return discountPrice;
    },
    activeSalePrice() {
      const { activeDiscountPrice } = this;
      const { price } = this.normalizedVariantActive;

      return calcSalePrice(price, activeDiscountPrice);
    },
    formattedActiveSalePrice() {
      return formatPriceWithoutZeros(this.activeSalePrice);
    },
    formattedWarrantyPrice() {
      const price =
        this.variantActiveWarrantyConfig?.variants.reduce(
          (acc, variant) => (variant.price ? acc + variant.price : acc),
          0
        ) || null;

      return formatPriceWithoutDecimals(price);
    },
    unavailableItem() {
      if (this.isBundle) {
        return findUnavailableBundleVariant(this.variantActiveBundleConfig);
      }

      if (!this.normalizedVariantActive.available) {
        return this.normalizedVariantActive;
      }

      return null;
    },
    hasWarrantyProduct() {
      return Boolean(this.variantActiveWarrantyConfig);
    },
  },
  methods: {
    isVariantInStock(normalizedVariant) {
      if (this.isBundle) {
        const areAllVariantsAvailable = isBundleAvailable(
          this.bundleConfig[normalizedVariant.id]
        );

        return normalizedVariant.available && areAllVariantsAvailable;
      }

      const { inventoryQuantity, inventoryPolicy } =
        this.variantsInventory[normalizedVariant.id];

      return (
        inventoryPolicy === INVENTORY_POLICIES.CONTINUE || inventoryQuantity > 0
      );
    },
    productToggleVideo(event) {
      const element = event.currentTarget;

      videoToggle(element);
    },
    productEndedVideo(event) {
      const videoEl = event.target;

      videoEnded(videoEl);
    },
    changeActiveProduct(productId) {
      const productNext =
        this.normalizedRelatedProducts.find(({ id }) => id === productId) ||
        null;

      if (productNext) {
        this.normalizedProductActive = productNext;
        this.normalizedVariantActive = findActiveVariantByOption(
          this.normalizedProductActive.variants,
          this.variantActiveOption
        );
        this.errorMessage = '';

        Vue.nextTick(() => {
          this.imagesSliderMain.refresh();
          this.imagesSliderThumbs.refresh();
          this.imagesSliderMain.go(0);

          updateProductUrl(
            this.normalizedVariantActive.id,
            this.normalizedProductActive.url
          );

          setMaxHeightStyle(
            '[data-product-base-dimmer-product-mobile-panel-features-content]'
          );
          updateAvailabilityAddToCartNavBtns();
        });
      }
    },
    changeActiveVariant(variantId, index) {
      const variantNext =
        this.normalizedProductActive.variants.find(
          (variant) => variant.id === variantId
        ) || null;

      if (variantNext) {
        this.normalizedVariantActive = variantNext;
        this.variantActiveOption =
          this.normalizedVariantActive.handleizedOption1;
        this.variantActiveIndex = index;
        this.errorMessage = '';
      }

      Vue.nextTick(() => {
        this.imagesSliderMain.refresh();
        this.imagesSliderThumbs.refresh();

        updateProductUrl(
          this.normalizedVariantActive.id,
          this.normalizedProductActive.url
        );

        setMaxHeightStyle(
          '[data-product-base-dimmer-product-mobile-panel-features-content]'
        );
      });
    },
    calcVariantsForSellBuyList(withWarranty) {
      let mainProducts = [];

      if (this.isBundle) {
        mainProducts = calcBundleVariantsForSell(
          this.bundleConfig[this.normalizedVariantActive.id]
        );
      } else if (withWarranty) {
        mainProducts = calcBundleVariantsForSell(
          this.variantActiveWarrantyConfig,
          false
        );
      } else {
        mainProducts.push({
          id: this.normalizedVariantActive.id,
          quantity: 1,
          properties: {},
        });
      }

      const bogoProducts = calcBogoBonusProducts(
        productsConfig.bogo[this.normalizedVariantActive.id]
      );

      if (bogoProducts) {
        return [...bogoProducts, ...mainProducts];
      }

      return mainProducts;
    },
    async submitProductForm(e, withWarranty = false) {
      e.preventDefault();

      this.isSpinnerMainProductActive = true;
      showBannerSpinner(true);

      await initCart();

      const items = this.calcVariantsForSellBuyList(withWarranty);

      try {
        await addItems(items);

        const { sideCart } = window;

        sideCart.updateView(null, openSideCart);

        sendCustomEvent({
          category: 'Bundles Behavior',
          action: 'click',
          label: `Add to cart ${this.normalizedProductActive.title}`,
        });
      } catch (err) {
        if (err.headers) {
          err.json().then((body) => {
            this.errorMessage = body.message;
          });
        } else {
          this.errorMessage = ADD_PRODUCT_TO_CART_ERROR_MSG;
        }
      }

      this.isSpinnerMainProductActive = false;
      showBannerSpinner(false);
    },
    calcFirstVariantId(productId) {
      return this.normalizedRelatedProducts.find(({ id }) => id === productId)
        ?.variants[0].id;
    },
    calcFormattedSalePrice(price, discountPrice = null) {
      const salePrice = calcSalePrice(price, discountPrice);

      return formatPriceWithoutZeros(salePrice);
    },
    calcFormattedNetPrice(price, compareAtPrice = null, discountPrice = null) {
      const netPrice = calcNetPrice(price, compareAtPrice, discountPrice);

      return formatPriceWithoutZeros(netPrice);
    },
    makeTooltipText(normalizedVariant) {
      return `${normalizedVariant.handleizedOption1.toUpperCase()}`;
    },
    openWishListModal() {
      window.productWishListModal?.openModal();
    },
    selectPanelImages(product) {
      const relatedVariant =
        productsConfig.products[product.id].variants[this.variantActiveIndex];

      return productsConfig.variantsMedia[relatedVariant.id].selectModelMedia;
    },
  },
  mounted() {
    updateAvailabilityAddToCartNavBtns();

    const variantIdByUrl = getVariantIdByUrl(
      productsConfig.products[productsConfig.activeProductId]
    );

    updateProductUrl(variantIdByUrl, this.normalizedProductActive.url);

    const mainSliderEl = document.querySelector('[data-product-slider-main]');
    const thumbsSliderEL = document.querySelector(
      '[data-product-slider-thumbs]'
    );

    if (mainSliderEl && thumbsSliderEL) {
      this.imagesSliderMain = initSlider('[data-product-slider-main]');
      this.imagesSliderThumbs = initThumbSlider('[data-product-slider-thumbs]');

      if (this.imagesSliderMain) {
        this.imagesSliderMain.on('active', ({ index }) => {
          const isVideoType = this.sliderMedias[index]?.type === 'video';

          if (isVideoType) {
            forceProductVideoStart(index, DeviceHelper.isMobileViewport());
          }
        });

        this.imagesSliderMain.on('lazyload:loaded', () => {
          this.loaded = true;
        });
        this.imagesSliderMain.sync(this.imagesSliderThumbs);
        this.imagesSliderMain.mount();
        this.imagesSliderThumbs.mount();
      }
    }

    forceProductVideoStart(0, DeviceHelper.isMobileViewport());

    window.addEventListener('load', () => {
      if (!this.isTabletViewport && mainSliderEl) {
        const mainImageSliderHeight = mainSliderEl.offsetHeight;

        thumbsSliderEL.style.height = `${mainImageSliderHeight}px`;
      }

      initProductBuyBanner({
        productId: this.normalizedProductActive.id,
        variantId: this.normalizedVariantActive.id,
        productsList: this.buyBannerProducts,
        swatchesList: this.swatchesList,
        formattedSalePrice: this.formattedActiveSalePrice || null,
        hasCrossedPrice: Boolean(
          this.activeDiscountPrice ||
            this.normalizedVariantActive.compare_at_price
        ),
        formattedNetPrice: this.formattedActiveNetPrice || null,
        isVariantActiveAvailable: this.isVariantActiveAvailable,
        submitButtonText: this.submitButtonText,
      });

      initProductWishListModal({
        variantId: this.unavailableItem?.id || null,
      });
    });

    window.addEventListener(
      'resize',
      throttle(() => {
        this.isTabletViewport = DeviceHelper.isTabletViewport();
        if (!this.isTabletViewport && mainSliderEl) {
          const mainImageSliderHeight = mainSliderEl.offsetHeight;

          thumbsSliderEL.style.height = `${mainImageSliderHeight}px`;
        }
      }, 100)
    );
  },
  updated() {
    const { productWishListModal, productBuyBanner } = window;

    productBuyBanner?.updateBannerStates({
      newProductId: this.normalizedProductActive.id,
      newVariantId: this.normalizedVariantActive.id,
      newProductsList: this.buyBannerProducts,
      newSwatchesList: this.swatchesList,
      newFormattedSalePrice: this.formattedActiveSalePrice || null,
      hasNewCrossedPrice: Boolean(
        this.activeDiscountPrice ||
          this.normalizedVariantActive.compare_at_price
      ),
      newFormattedNetPrice: this.formattedActiveNetPrice || null,
      isNewVariantAvailable: this.isVariantActiveAvailable,
      newSubmitButtonText: this.submitButtonText,
    });

    if (this.unavailableItem) {
      productWishListModal?.updateModalState({
        newVariantId: this.unavailableItem.id,
      });
    }
  },
};

export const initProduct = () => {
  if (Product) {
    new Vue(Product).$mount('#product-base-dimmer');
  }

  window.addEventListener(
    'resize',
    debounce(() => {
      setMaxHeightStyle(
        '[data-product-base-dimmer-product-mobile-panel-features-content]'
      );
      addOffsetToStickyProductSlider(
        '[data-product-base-dimmer-sticky-gallery]'
      );
    }, 50)
  );

  window.addEventListener(
    'scroll',
    throttle(() => {
      addOffsetToStickyProductSlider(
        '[data-product-base-dimmer-sticky-gallery]'
      );
    }, 100)
  );

  document.addEventListener('DOMContentLoaded', () => {
    setMaxHeightStyle(
      '[data-product-base-dimmer-product-mobile-panel-features-content]'
    );
    addOffsetToStickyProductSlider('[data-product-base-dimmer-sticky-gallery]');
  });
};
